import { useMemo, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Bundle } from '@prisma/client';
import { AutoCompleteProps, Input, Popover, Spin, Typography } from 'antd';
import { debounce } from 'lodash';

import { useFindBundle } from '../../data';

const exampleContent = (
  <>
    <div>
      <Typography.Text strong>We recommend using the following Naming Convention: </Typography.Text>
    </div>
    <div>
      <Typography.Text>
        [Product Family Name or Die Name]_[die information or description]_[sub org team or function]_[Badge Type]
      </Typography.Text>
    </div>
    <div>
      <Typography.Text type="warning">
        Recommend using underscores. Do not use characters such as / - ! * etc
      </Typography.Text>
    </div>
    <Typography.Text underline>Examples</Typography.Text>
    <Typography.Text italic>
      <div>FalconShore_SOP_FE_BlueBadge </div>
      <div> Morganville_FE_GreenBadge, NVL_PCDS_FE_BB</div>
      <div> NVL_PCDS_FE_BB</div>
    </Typography.Text>
  </>
);

export type BundleNameInputProps = Omit<AutoCompleteProps, 'loading' | 'options' | 'onSearch' | 'onSelect'>;

export const useBundleNameAutoComplete = () => {
  const [searchText, setSearchText] = useState('');
  const { data, isPending } = useFindBundle<Bundle>({
    where: {
      isActive: true,
      name: {
        contains: searchText.trim(),
        mode: 'insensitive',
      },
    },
    select: {
      id: true,
      name: true,
    },
    orderBy: {
      name: 'asc',
    },
    skip: 0,
    take: 50,
  });

  const handleOnSearch = useMemo(() => debounce(setSearchText, 500), []);

  const getProps = (props?: Omit<AutoCompleteProps, 'children'>): AutoCompleteProps => {
    const options = props?.options ?? [];
    data?.data.forEach((bundle) => options.push({ value: bundle.name, label: bundle.name }));

    const onSearch = (text: string) => {
      handleOnSearch(text);
      props?.onSearch && props.onSearch(text);
    };

    return {
      ...props,
      children: (
        <Input
          placeholder="[Product Family Name or Die Name]_[die information or description]_[sub org team or
              function]_[Badge Type]"
          suffix={
            isPending ? (
              <Spin size="small" />
            ) : (
              <Popover placement="bottomRight" trigger="hover" content={exampleContent}>
                <InfoCircleOutlined />
              </Popover>
            )
          }
        />
      ),
      onSearch,
      options,
    };
  };

  return {
    getProps,
  };
};
